import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApplications, withdrawApplication } from '../api/applications';
import { fetchSavedJobs, removeSavedJob, saveJob } from '../api/savedJobs';
import { fetchJobMatches, sendMatchFeedback } from '../api/jobMatches';

export function useApplicationsQuery() {
  return useQuery({
    queryKey: ['candidate-applications'],
    queryFn: fetchApplications,
    staleTime: 1000 * 30,
  });
}

export function useWithdrawApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (applicationId: string | number) => withdrawApplication(applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidate-applications'] });
    },
  });
}

export function useSavedJobsQuery() {
  return useQuery({
    queryKey: ['candidate-saved-jobs'],
    queryFn: fetchSavedJobs,
    staleTime: 1000 * 30,
  });
}

export function useRemoveSavedJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string | number) => removeSavedJob(jobId),
    onSuccess: (_, jobId) => {
      queryClient.setQueryData(['candidate-saved-jobs'], (current: unknown) => {
        if (!Array.isArray(current)) return current;
        return current.filter((job) => job.id !== jobId);
      });
    },
  });
}

export function useSaveJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string | number) => saveJob(jobId),
    onSuccess: (savedJob) => {
      queryClient.setQueryData(['candidate-saved-jobs'], (current: unknown) => {
        if (!Array.isArray(current)) return [savedJob];
        const exists = current.some((job) => job.id === savedJob.id);
        if (exists) {
          return current;
        }
        return [savedJob, ...current];
      });
    },
  });
}

export function useJobMatchesQuery() {
  return useQuery({
    queryKey: ['candidate-job-matches'],
    queryFn: fetchJobMatches,
    staleTime: 1000 * 30,
  });
}

export function useMatchFeedback() {
  return useMutation({
    mutationFn: (payload: { matchId: string | number; feedback: 'positive' | 'negative' }) =>
      sendMatchFeedback(payload.matchId, payload.feedback),
  });
}
